import { AuthenticationDetails, CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import QRCode from 'qrcode';
import { installGatewayFrom } from './dbGateway';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Legacy export/call names are retained; no Supabase client is instantiated.
const pool = new CognitoUserPool({
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_APP_CLIENT_ID,
});
const listeners = new Set();
const TOTP = 'SOFTWARE_TOKEN_MFA';
let pending = null;
let enrollment = null;
let sessionRequest = null;
let lastToken = null;
let generation = 0;

const ok = (data) => ({ data, error: null });
const failure = (error, data = null) => ({ data, error });
const attempt = async (fn) => {
  try { return ok(await fn()); } catch (error) { return failure(error); }
};
function emit(event, session = null) {
  listeners.forEach(cb => { Promise.resolve().then(() => cb(event, session)).catch(() => {}); });
}
function sessionShape(session) {
  const claims = session.getIdToken().decodePayload();
  return {
    access_token: session.getAccessToken().getJwtToken(),
    id_token: session.getIdToken().getJwtToken(),
    token_type: 'bearer',
    expires_at: session.getAccessToken().getExpiration(),
    user: { id: claims.sub, sub: claims.sub, email: claims.email, user_metadata: { full_name: claims.name } },
  };
}
function clearSession() {
  generation += 1;
  pool.getCurrentUser()?.signOut();
  pending = null;
  enrollment = null;
  sessionRequest = null;
  lastToken = null;
  emit('SIGNED_OUT');
}
function authenticationError(error) {
  if (['NotAuthorizedException', 'UserNotFoundException'].includes(error?.code || error?.name)) {
    return Object.assign(new Error('We couldn’t sign you in. Check your email or username and password.'), { code: 'INVALID_CREDENTIALS' });
  }
  return error;
}

async function resolveIdentifier(identifier) {
  const value = (identifier || '').trim();
  if (!value) throw new Error('Enter your email or username.');
  if (value.includes('@')) return value.toLowerCase();
  const response = await fetch(`${API}/auth/resolve-username`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ username: value }),
  });
  // A Cognito-native username is also supported if there is no application alias.
  if (response.status === 404) return value;
  if (!response.ok) throw new Error('Unable to connect. Please try again.');
  const data = await response.json();
  return data.email || value;
}

function authCallbacks(user, resolve, version = generation) {
  const challenge = (type, extra = {}) => {
    if (version !== generation) return resolve(failure(new Error('Sign-in cancelled. Please start again.')));
    pending = { user, type, version, ...extra };
    resolve(ok({ user: null, session: null, challenge: type, requiredAttributes: extra.requiredAttributes || [] }));
  };
  return {
    onSuccess(session) {
      if (version !== generation) {
        user.signOut();
        return resolve(failure(new Error('Sign-in cancelled. Please start again.')));
      }
      pending = null;
      enrollment = null;
      const shaped = sessionShape(session);
      lastToken = shaped.access_token;
      emit('SIGNED_IN', shaped);
      resolve(ok({ user: shaped.user, session: shaped }));
    },
    onFailure: error => resolve(failure(authenticationError(error))),
    newPasswordRequired: (_attributes, requiredAttributes) => challenge('NEW_PASSWORD_REQUIRED', { requiredAttributes }),
    totpRequired: () => challenge(TOTP),
    mfaRequired: () => challenge('SMS_MFA'),
    mfaSetup: () => challenge('MFA_SETUP'),
    selectMFAType: (_name, params) => {
      let choices = [];
      try { choices = JSON.parse(params.MFAS_CAN_CHOOSE || '[]'); } catch (_) { /* unsupported selection */ }
      const selection = choices.includes(TOTP) ? TOTP : choices.includes('SMS_MFA') ? 'SMS_MFA' : null;
      if (!selection) return resolve(failure(new Error('This verification method is not supported. Contact your administrator.')));
      user.sendMFASelectionAnswer(selection, authCallbacks(user, resolve, version));
    },
    customChallenge: () => resolve(failure(new Error('This account needs an unsupported verification step. Contact your administrator.'))),
  };
}

const auth = {
  async getSession() {
    if (sessionRequest) return sessionRequest;
    const user = pool.getCurrentUser();
    if (!user) return ok({ session: null });
    const version = generation;
    const request = new Promise(resolve => user.getSession((error, session) => {
      if (version !== generation) return resolve(ok({ session: null }));
      if (error || !session?.isValid()) {
        // Do not discard a refresh token on a temporary network failure.
        if (['NotAuthorizedException', 'UserNotFoundException'].includes(error?.code || error?.name)) clearSession();
        return resolve(failure(error || new Error('Your session expired. Please sign in again.'), { session: null }));
      }
      const shaped = sessionShape(session);
      if (lastToken && lastToken !== shaped.access_token) emit('TOKEN_REFRESHED', shaped);
      lastToken = shaped.access_token;
      resolve(ok({ session: shaped }));
    }));
    sessionRequest = request;
    try { return await request; } finally { if (sessionRequest === request) sessionRequest = null; }
  },
  async getUser() {
    const { data, error } = await auth.getSession();
    return { data: { user: data?.session?.user || null }, error };
  },
  onAuthStateChange(callback) {
    listeners.add(callback);
    return { data: { subscription: { unsubscribe: () => listeners.delete(callback) } } };
  },
  async signInWithPassword({ email, password }) {
    try {
      pending = null;
      enrollment = null;
      const version = ++generation;
      const username = await resolveIdentifier(email);
      const user = new CognitoUser({ Username: username, Pool: pool });
      user.setAuthenticationFlowType('USER_SRP_AUTH');
      return await new Promise(resolve => user.authenticateUser(
        new AuthenticationDetails({ Username: username, Password: password }),
        authCallbacks(user, resolve, version),
      ));
    } catch (error) { return failure(authenticationError(error)); }
  },
  async completeNewPassword(password, attributes = {}) {
    if (pending?.type !== 'NEW_PASSWORD_REQUIRED') return failure(new Error('Your invitation session expired. Sign in with your temporary password again.'));
    const { user, requiredAttributes, version } = pending;
    const required = {};
    for (const name of requiredAttributes || []) {
      if (!attributes[name]) return failure(new Error(`Please enter ${name.replace(/_/g, ' ')}.`));
      required[name] = attributes[name];
    }
    return new Promise(resolve => user.completeNewPasswordChallenge(password, required, authCallbacks(user, resolve, version)));
  },
  async confirmSignIn(code) {
    if (!pending || ![TOTP, 'SMS_MFA'].includes(pending.type)) return failure(new Error('Your sign-in session expired. Please sign in again.'));
    const { user, type, version } = pending;
    return new Promise(resolve => user.sendMFACode(code, authCallbacks(user, resolve, version), type));
  },
  cancelChallenge() { pending = null; enrollment = null; generation += 1; },
  async signOut() { clearSession(); return ok(null); },
  async resetPasswordForEmail(identifier) {
    // Preserve the existing server-side audit, throttling and lockout reset.
    // This endpoint now invokes Cognito forgot_password, not Supabase.
    return attempt(async () => {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: identifier.trim() }),
      });
      if (!response.ok) throw new Error('Unable to request a code. Please try again.');
      return { sent: true }; // Neutral response: never confirms account existence/delivery.
    });
  },
  async confirmPassword(identifier, code, password) {
    return attempt(async () => {
      const username = await resolveIdentifier(identifier);
      const user = new CognitoUser({ Username: username, Pool: pool });
      await new Promise((resolve, reject) => user.confirmPassword(code, password, { onSuccess: resolve, onFailure: reject }));
      clearSession();
      return null;
    });
  },
  async updateUser({ password, currentPassword }) {
    return attempt(async () => {
      if (!password || !currentPassword) throw new Error('Enter your current and new password.');
      const user = await authenticatedUser();
      await new Promise((resolve, reject) => user.changePassword(currentPassword, password, (error, data) => error ? reject(error) : resolve(data)));
      const { data } = await auth.getUser();
      return data;
    });
  },
  // No browser registration method: accounts are provisioned by admins.
  mfa: {
    async listFactors() {
      return attempt(async () => {
        const user = await authenticatedUser();
        const data = await new Promise((resolve, reject) => user.getUserData((error, data) => error ? reject(error) : resolve(data), { bypassCache: true }));
        const totp = (data.UserMFASettingList || []).includes(TOTP)
          ? [{ id: TOTP, factor_type: 'totp', status: 'verified', friendly_name: 'Authenticator app' }] : [];
        return { all: totp, totp };
      });
    },
    async enroll() {
      return attempt(async () => {
        const user = pending?.type === 'MFA_SETUP' ? pending.user : await authenticatedUser();
        const secret = await new Promise((resolve, reject) => user.associateSoftwareToken({ associateSecretCode: resolve, onFailure: reject }));
        enrollment = { user, id: TOTP, version: generation, challengeId: null };
        const uri = `otpauth://totp/${encodeURIComponent(`Operon:${user.getUsername()}`)}?secret=${encodeURIComponent(secret)}&issuer=Operon&algorithm=SHA1&digits=6&period=30`;
        return { id: TOTP, totp: { secret, qr_code: await QRCode.toDataURL(uri, { width: 240, margin: 2 }) } };
      });
    },
    async challenge({ factorId }) {
      // Cognito has one software token, not independent Supabase factor IDs.
      // This nonce only correlates the local enrollment; Cognito verifies the code.
      if (!enrollment || factorId !== TOTP) return failure(new Error('Start authenticator setup again.'));
      enrollment.challengeId = crypto.randomUUID();
      return ok({ id: enrollment.challengeId });
    },
    async verify({ factorId, challengeId, code }) {
      if (!enrollment || factorId !== TOTP || challengeId !== enrollment.challengeId || enrollment.version !== generation) return failure(new Error('Start authenticator setup again.'));
      const { user, version } = enrollment;
      if (pending?.type === 'MFA_SETUP') {
        return new Promise(resolve => user.verifySoftwareToken(code, 'Operon', authCallbacks(user, resolve, version)));
      }
      return attempt(async () => {
        await new Promise((resolve, reject) => user.verifySoftwareToken(code, 'Operon', { onSuccess: resolve, onFailure: reject }));
        await new Promise((resolve, reject) => user.setUserMfaPreference(null, { Enabled: true, PreferredMfa: true }, (error, data) => error ? reject(error) : resolve(data)));
        enrollment = null;
        return null;
      });
    },
    async unenroll({ factorId }) {
      return attempt(async () => {
        if (factorId !== TOTP) throw new Error('Unsupported authenticator.');
        const user = await authenticatedUser();
        await new Promise((resolve, reject) => user.setUserMfaPreference(null, { Enabled: false, PreferredMfa: false }, (error, data) => error ? reject(error) : resolve(data)));
        enrollment = null;
        return null;
      });
    },
  },
};

async function authenticatedUser() {
  const { data, error } = await auth.getSession();
  if (error) throw error;
  if (!data.session) throw new Error('Please sign in again.');
  const user = pool.getCurrentUser();
  await new Promise((resolve, reject) => user.getSession((err, session) => err ? reject(err) : resolve(session)));
  return user;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    const prefix = `CognitoIdentityServiceProvider.${pool.getClientId()}.`;
    if (event.key !== null && !event.key.startsWith(prefix)) return;
    if (!pool.getCurrentUser()) {
      generation += 1;
      pending = enrollment = sessionRequest = lastToken = null;
      emit('SIGNED_OUT');
    } else if (event.key?.endsWith('.LastAuthUser') && event.newValue !== event.oldValue) {
      auth.getSession().then(({ data }) => { if (data.session) emit('SIGNED_IN', data.session); });
    }
  });
}

export const supabase = { auth };
installGatewayFrom(supabase, API);

export async function authedFetch(path, options = {}) {
  const { data: { session }, error } = await auth.getSession();
  if (error) throw error;
  if (!session) throw new Error('Please sign in again.');
  let viewingOrg = null;
  try { viewingOrg = localStorage.getItem('viewing_org_id'); } catch (_) { /* ignore */ }
  const headers = {
    'Content-Type': 'application/json',
    ...(viewingOrg ? { 'X-Viewing-Org-Id': viewingOrg } : {}),
    ...(options.headers || {}),
    Authorization: `Bearer ${session.access_token}`,
  };
  const resp = await fetch(`${API}${path}`, { ...options, headers });
  if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
  return resp.json();
}

export async function uploadFile(path, file, bucket = 'org-documents') {
  const { data: { session }, error } = await auth.getSession();
  if (error) throw error;
  if (!session) throw new Error('Please sign in again.');
  const fd = new FormData();
  fd.append('file', file);
  fd.append('bucket', bucket);
  fd.append('path', path);
  const resp = await fetch(`${API}/storage/upload`, {
    method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` }, body: fd,
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
}
